import { Link, Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
    LayoutDashboard,
    Building2,
    Users,
    UserRound,
    CreditCard,
    Banknote,
    Receipt,
    Globe,
    Key,
    HeartPulse,
    LogOut,
    Menu,
    X,
    Store,
    Bell,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/admin/alumnos', icon: UserRound, label: 'Alumnos' },
    { to: '/admin/gimnasios', icon: Building2, label: 'Gimnasios' },
    { to: '/admin/propietarios', icon: Users, label: 'Propietarios' },
    { to: '/admin/planes', icon: CreditCard, label: 'Planes' },
    { to: '/admin/pagos', icon: Banknote, label: 'Pagos' },
    { to: '/admin/suscripciones', icon: Receipt, label: 'Suscripciones' },
    { to: '/admin/mediciones', icon: HeartPulse, label: 'Mediciones' },
    { to: '/admin/seo', icon: Globe, label: 'SEO' },
    { to: '/admin/oauth', icon: Key, label: 'OAuth' },
    { to: '/admin/tienda', icon: Store, label: 'Tienda' },
    { to: '/admin/notificaciones', icon: Bell, label: 'Notificaciones' },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <div className="min-h-screen flex bg-black">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Sidebar */}
            <aside className={cn(
                "fixed inset-y-0 left-0 z-50 w-64 bg-black border-r border-hairline-dark transform transition-transform lg:translate-x-0 lg:static lg:z-auto",
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-4">
                        <h1 className="text-xl font-bold text-white uppercase tracking-wide">
                            <Link to="/" onClick={() => setSidebarOpen(false)}>GYMS.ar</Link>
                        </h1>
                        <Button variant="outline" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                            <X className="h-5 w-5" />
                        </Button>
                    </div>
                    <div className="px-4 pb-2">
                        <p className="text-xs text-white/60 uppercase tracking-wider">Superadmin</p>
                    </div>
                    <Separator />
                    <nav className="flex-1 p-3 space-y-1">
                        {navItems.map(item => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) => cn(
                                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                    isActive
                                        ? "bg-white/10 text-white"
                                        : "text-white hover:bg-white/5"
                                )}
                            >
                                <item.icon className="h-4 w-4" />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                    <Separator />
                    <div className="p-4 space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate text-white">{user?.name}</p>
                                <p className="text-xs text-white/60 truncate">{user?.email}</p>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" className="w-full justify-start" onClick={handleLogout}>
                            <LogOut className="h-4 w-4 mr-2" />
                            Cerrar sesión
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="sticky top-0 z-30 bg-black border-b border-hairline-dark px-4 py-3 flex items-center justify-between lg:hidden">
                    <div className="flex items-center">
                        <Button variant="outline" size="icon" onClick={() => setSidebarOpen(true)}>
                            <Menu className="h-5 w-5" />
                        </Button>
                        <h1 className="ml-3 text-lg font-bold uppercase tracking-wide text-white">
                            <Link to="/">GYMS.ar</Link>
                        </h1>
                    </div>
                </header>
                <main className="flex-1 p-4 md:p-6 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
